import type { DeckConfig, DeckProperties } from "@/types";
import { DeckFeature } from "@/types";
import { type InjectionKey, reactive, ref } from "vue";
import { mergeDeep } from "@/utils/objects";
import { CriticalError } from "@/utils/errors";

/**
 * ENV vars:
 *  - VITE_CONFIG_URL: configuration URL. Use wildcard {CONFIG_ID}
 */

export type ConfigStore = ReturnType<typeof useConfigStore>;
export const CONFIG: InjectionKey<ConfigStore> = Symbol("CONFIG");

export const useConfigStore = () => {
  const CONFIG_URL =
    process.env.VITE_CONFIG_URL ??
    import.meta.env.VITE_CONFIG_URL ??
    "https://avp-3dws-deck.eu-west-1-web-streaming.avp.tech/configs-from-cms/{CONFIG_ID}.json";

  const deckProperties = ref<DeckProperties>();
  const config = ref<Partial<DeckConfig>>();
  const verifiedConfig = ref<Partial<DeckConfig>>({});
  const url = ref<string | null>(CONFIG_URL);
  const initiallyFetched = ref(false);
  const configLoaded = ref(false);

  async function initializeConfig(props: DeckProperties) {
    // set initial config values from deck properties
    const { configId, features, streamingStage, market } = props;
    deckProperties.value = props;
    updateConfig(
      {
        streamingStage,
        market,
        features: JSON.parse(features ?? "{}")
      },
      false
    );

    const urlContainsPlaceholder = url.value && url.value.indexOf("{CONFIG_ID}") > -1;

    // if configId is provided and the URL contains a placeholder for it, replace it
    if (!!configId && urlContainsPlaceholder) {
      url.value = url.value!.replace("{CONFIG_ID}", configId);
    }

    await loadConfig();
  }

  async function loadConfig() {
    // if config exists return existing config
    if (initiallyFetched.value) {
      return config.value;
    }

    const newConfig = await fetchConfig();
    updateConfig(newConfig, false);
    configLoaded.value = true;
    initiallyFetched.value = true;
  }

  async function fetchConfig() {
    if (!url.value) {
      throw new CriticalError(`No configuration URL provided.`);
    }

    configLoaded.value = false;

    let response: Response;
    try {
      response = await fetch(url.value);
      return await response.json();
    } catch (error) {
      throw new CriticalError(`Failed to fetch configuration from ${url.value}. ${error}`);
    }
  }

  function isFeatureEnabled(feature: keyof typeof DeckFeature): boolean {
    return !!config.value?.features?.[feature];
  }

  function updateConfig(partialConfig: Partial<DeckConfig>, isOverride = true) {
    if (partialConfig?.streamingStage === "prelive") {
      partialConfig.streamingStage = "prelive-euw1";
    }

    if (!config.value) {
      config.value = partialConfig;
    }

    config.value = isOverride
      ? mergeDeep(config.value, partialConfig)
      : mergeDeep(partialConfig, config.value);
  }

  function getInitialEnvironment(): string | undefined {
    const configuredInitialEnvironment = config.value?.initialEnvironment;
    if (configuredInitialEnvironment) {
      return configuredInitialEnvironment;
    }

    const configuredEnvironments = config.value?.environments;
    if (configuredEnvironments) {
      return configuredEnvironments[0];
    }

    const configuredDayEnvironment = config.value?.dayEnvironment;
    if (configuredDayEnvironment) {
      return configuredDayEnvironment;
    }

    const configuredNightEnvironment = config.value?.nightEnvironment;
    if (configuredNightEnvironment) {
      return configuredNightEnvironment;
    }
  }

  function getInitialCamera(): string | undefined {
    if (deckProperties.value?.camera) {
      return deckProperties.value.camera;
    }

    const configuredInitialCamera = config.value?.initialCamera;
    if (configuredInitialCamera) {
      return configuredInitialCamera;
    }

    const configuredInteriorCameras = config.value?.interiorCameras ?? [];
    const configuredExteriorCameras = config.value?.exteriorCameras ?? [];
    const allConfiguredCameras = [...configuredExteriorCameras, ...configuredInteriorCameras];
    if (allConfiguredCameras?.length) {
      return allConfiguredCameras[0];
    }
  }

  return reactive({
    config,
    verifiedConfig: verifiedConfig,
    loadConfig: loadConfig,
    updateConfig: updateConfig,
    isFeatureEnabled: isFeatureEnabled,
    initializeConfig: initializeConfig,
    getInitialEnvironment: getInitialEnvironment,
    getInitialCamera: getInitialCamera,
    configLoaded: configLoaded
  });
};
